import type { ReactNode } from "react";

import { cn } from "@/lib/utils";
import { STATUS_LABEL, STATUS_TONE, type OrderStatus } from "@/lib/orders";

/** A toggle pill: the on-screen twin of the printed order's toggles. */
export function Toggle({
  on,
  label,
  value,
  onClick,
  tone = "purple",
  disabled,
  className,
}: {
  on: boolean;
  label: ReactNode;
  value?: ReactNode;
  onClick?: () => void;
  tone?: "purple" | "gold" | "green";
  disabled?: boolean;
  className?: string;
}) {
  const onTone = {
    purple: "border-pink bg-pink/20 text-paper",
    gold: "border-gold bg-gold/25 text-paper",
    green: "border-green bg-green/20 text-paper",
  }[tone];
  const dot = { purple: "bg-pink border-pink", gold: "bg-gold border-gold", green: "bg-green border-green" }[tone];
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-pressed={on}
      className={cn(
        "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-left text-[0.78rem] transition disabled:opacity-60",
        on ? onTone : "border-white/15 bg-white/5 text-ice/75 hover:border-white/35",
        className,
      )}
    >
      <span className={cn("h-3.5 w-3.5 shrink-0 rounded-full border", on ? dot : "border-white/40")} />
      <span className="leading-tight">{label}</span>
      {value !== undefined && <b className={cn("ml-auto pl-2 font-semibold", on ? "text-goldbright" : "text-ice/55")}>{value}</b>}
    </button>
  );
}

export function Card({
  tag,
  children,
  className,
  right,
}: {
  tag: string;
  children: ReactNode;
  className?: string;
  right?: ReactNode;
}) {
  return (
    <section className={cn("glass-panel relative mt-4 p-5 pt-6", className)}>
      <span className="absolute -top-2.5 left-4 rounded-full bg-gradient-to-r from-violet to-magenta px-3 py-0.5 text-[0.58rem] font-semibold uppercase tracking-[0.2em] text-paper">
        {tag}
      </span>
      {right && <div className="absolute -top-3 right-4">{right}</div>}
      {children}
    </section>
  );
}

export function Label({ children }: { children: ReactNode }) {
  return <span className="field-label">{children}</span>;
}

export function OrderStatusPill({ status, className }: { status: OrderStatus; className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-[0.14em]",
        STATUS_TONE[status],
        className,
      )}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

export const BTN = "inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-[0.66rem] font-semibold uppercase tracking-[0.14em] transition disabled:opacity-50";
export const BTN_PRIMARY = `${BTN} bg-magenta text-paper hover:brightness-110`;
export const BTN_GOLD = `${BTN} bg-gold text-night hover:brightness-110`;
export const BTN_GREEN = `${BTN} bg-[#25D366] text-[#0b141a] hover:brightness-105`;
export const BTN_GHOST = `${BTN} border border-white/20 text-ice/80 hover:border-white/40 hover:text-paper`;
